import { useTranslation } from 'react-i18next'
import { Check, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useChat } from './ChatProvider'

interface Props {
  /** Opens the blocked people list (the host decides where it shows). */
  onOpenBlocks: () => void
  align?: 'start' | 'end'
  side?: 'top' | 'bottom'
  className?: string
  /** Unused since F-184 (the status hint lives under the status picker); kept for the callers. */
  hint?: boolean
}

/**
 * The chat's settings menu (a gear since F-184; it was a ⋯): "Confirmação de leitura", "Sons" and the blocked people list.
 * F-184: the status (Disponível, Ocupado, Indisponível) left this menu for the
 * picker in the avatar sidebar's "me" block (`StatusSelect`).
 */
export const ChatSettingsMenu = ({ onOpenBlocks, align = 'end', side = 'bottom', className }: Props) => {
  const { t } = useTranslation('chat')
  const chat = useChat()

  return (
    <div className={cn('relative', className)} onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8 rounded-md text-gray-600 hover:text-gray-900"
            aria-label={t('settings')}
            title={t('settings')}
            data-chat-settings
          >
            <Settings className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} side={side} className="z-[60] w-64">
          <DropdownMenuItem onClick={() => chat.updateMe({ chatReadReceipts: !chat.me?.chatReadReceipts }).catch(() => {})} data-chat-read-receipts-toggle>
            <span className="flex-1">
              <span className="block text-sm">{t('readReceipts.label')}</span>
              <span className="block text-xs text-gray-500">{t('readReceipts.help')}</span>
            </span>
            {chat.me?.chatReadReceipts && <Check className="ml-2 h-4 w-4 shrink-0 text-gray-500" />}
          </DropdownMenuItem>
          {/* F-184: only once the API knows the preference. */}
          {typeof chat.me?.chatShowLastSeen === 'boolean' && (
            <DropdownMenuItem onClick={() => chat.updateMe({ chatShowLastSeen: !chat.me?.chatShowLastSeen }).catch(() => {})} data-chat-last-seen-toggle>
              <span className="flex-1">
                <span className="block text-sm">{t('lastSeen.settingLabel')}</span>
                <span className="block text-xs text-gray-500">{t('lastSeen.settingHelp')}</span>
              </span>
              {chat.me?.chatShowLastSeen && <Check className="ml-2 h-4 w-4 shrink-0 text-gray-500" />}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => chat.setSoundsOn(!chat.soundsOn)} data-chat-sounds-toggle>
            {t('sounds.label')}
            {chat.soundsOn && <Check className="ml-auto h-4 w-4 text-gray-500" />}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onOpenBlocks} data-chat-blocks-open>
            {t('blocks.title')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
